import { useNavigate } from "@solidjs/router";
import { useLayout } from "@storytell/ui/layout";
import { TbBell } from "solid-icons/tb";
import { type Component, For, onMount, Show } from "solid-js";
import { useI18n } from "~/i18n/context";
import { trackFeature } from "~/lib/trackFeature";
import { useWire } from "~/wire";
import { NotificationItem } from "./internal/NotificationItem";
import { RunningChatsSection } from "./internal/RunningChatsSection";
import { useRunningChats } from "./useRunningChats";

export const NotificationPanel: Component = () => {
  const wire = useWire();
  const navigate = useNavigate();
  const { t } = useI18n();
  const notifications = wire.notifications;
  const running = useRunningChats();
  const layout = useLayout();

  // The popover used to load on open; a routed panel has to ask for itself.
  onMount(() => notifications.ensureLoaded());

  return (
    <div class="flex size-full flex-col" classList={{ "mx-auto max-w-2xl px-4": !layout.isSide() }}>
      <div class="flex items-center justify-between border-b border-primary-border px-4 py-3">
        <span class="text-sm font-semibold text-primary-text">{t("notifications.panel.title")}</span>
        <button
          type="button"
          onClick={() => {
            trackFeature({ feature: "notifications", action: "mark_all", via: "button" });
            notifications.markAllRead();
          }}
          disabled={notifications.unreadCount() === 0}
          class="text-xs font-medium text-brand transition-colors hover:text-brand-hover disabled:cursor-default disabled:text-muted-text hover:disabled:text-muted-text"
        >
          {t("notifications.panel.mark_all_read")}
        </button>
      </div>

      <div class="min-h-0 flex-1 overflow-y-auto">
        <RunningChatsSection />
        <Show
          when={notifications.list().length > 0}
          fallback={
            <Show when={!notifications.listLoading()}>
              <div class="flex flex-col items-center gap-2 px-4 py-12 text-center">
                <TbBell class="size-6 text-muted-text" aria-hidden="true" />
                <span class="text-sm text-secondary-text">{t("notifications.panel.empty")}</span>
              </div>
            </Show>
          }
        >
          {/* "Earlier" only makes sense opposite a visible live group. */}
          <Show when={running().length > 0}>
            <h2 class="px-4 pt-3 pb-1 text-xs font-semibold tracking-wide text-muted-text uppercase">
              {t("notifications.earlier.title")}
            </h2>
          </Show>
          <For each={notifications.list()}>{(n) => <NotificationItem notification={n} />}</For>
          <Show when={notifications.hasMore()}>
            <div class="p-2">
              <button
                type="button"
                onClick={() => void notifications.loadMore()}
                class="w-full rounded-md py-2 text-xs font-medium text-brand transition-colors hover:bg-panel-hover"
              >
                {t("notifications.panel.load_more")}
              </button>
            </div>
          </Show>
        </Show>
      </div>

      <div class="border-t border-primary-border">
        <button
          type="button"
          onClick={() => {
            wire.ui.layout.setNotificationsOpen(false);
            navigate("/notifications");
          }}
          class="w-full py-2.5 text-center text-xs font-medium text-brand transition-colors hover:bg-panel-hover"
        >
          {t("notifications.panel.view_all")}
        </button>
      </div>
    </div>
  );
};
